import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import { useAccessPage } from "@ext/enterprise/components/admin/hooks/useAccessPage";
import { useSheetSlot } from "@ext/enterprise/components/admin/hooks/useSheetSlot";
import { useAccessSnapshot } from "@ext/enterprise/components/admin/settings/members/hooks/useAccessSnapshot";
import { type GesRepo, getRepoRowId } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { useResourceCatalogs } from "@ext/enterprise/components/admin/settings/resources/hooks/useResourceCatalogs";
import { toCatalogRow } from "@ext/enterprise/components/admin/settings/resources/model/CatalogRow";
import {
	catalogColumn,
	catalogColumnId,
} from "@ext/enterprise/components/admin/settings/resources/model/catalogColumn";
import { useRowSelectionWithData } from "@ext/enterprise/components/admin/ui-kit/table/useRowSelection";
import { useCallback, useMemo, useState } from "react";

export const useReposViewModel = () => {
	const { global } = useSettings();

	const { isLoading, retry, tabError } = useAccessPage();

	const { aggregate, isLoading: isSnapshotLoading, applyAndSave } = useAccessSnapshot({ enabled: !isLoading });

	const [isDeleting, setIsDeleteing] = useState(false);
	const repoCandidates = useMemo(() => {
		const existing = new Set(aggregate.repos.map((x) => x.id));
		return global?.allGitResources?.filter((x) => !existing.has(x));
	}, [aggregate.repos, global.allGitResources]);

	const bulkSlot = useSheetSlot<GesRepo[]>({ keyBase: "single" });
	const singleSlot = useSheetSlot<GesRepo | null>({ keyBase: "bulk" });

	const { rowSelection, setRowSelection, selectedRows } = useRowSelectionWithData(aggregate.repos, getRepoRowId);

	const selectedHasBase = useMemo(() => selectedRows.some((x) => x.isBase), [selectedRows]);

	const catalogs = useResourceCatalogs();

	const columns = useMemo(
		() => [
			catalogColumn<GesRepo>({
				getRow: (row) => toCatalogRow(row.id, catalogs.get(row.id)),
			}),
		],
		[catalogs],
	);

	const openBulkSelected = useCallback(async () => {
		bulkSlot.openWith(selectedRows);
	}, [bulkSlot.openWith, selectedRows]);

	const removeSelected = useCallback(async () => {
		await applyAndSave(
			selectedRows.map((x) => {
				return {
					kind: "removeResource",
					resourceId: x.id,
				};
			}),
		);
		setRowSelection({});
	}, [selectedRows, applyAndSave, setRowSelection]);

	const confirmDelete = useCallback(async () => {
		setIsDeleteing(true);
		try {
			await removeSelected();
		} finally {
			setIsDeleteing(false);
		}
	}, [removeSelected]);

	return {
		data: {
			rows: aggregate.repos,
			getId: getRepoRowId,
			columns,
			candidates: repoCandidates,
			rowSelection,
			setRowSelection,
			selected: selectedRows,
			selectedHasBase,
			searchColumnId: catalogColumnId,
			isLoading: isSnapshotLoading,
			delete: {
				isDeleting,
				confirm: confirmDelete,
			},
		},
		form: {
			isLoading,
			tabError,
			retry,
		},
		card: {
			bulk: { ...bulkSlot, openSelected: openBulkSelected },
			single: singleSlot,
		},
		aggregate,
		applyChanges: applyAndSave,
	};
};
